import { useMemo } from 'react';
import { flatten } from 'lodash';
import { useInfiniteQuery, useMutation } from 'react-query';
import * as api from '../../services/api';
import useAccount from './useAccount';

const HIDDEN_AUDIT_LOG_TYPES = ['EmployeeShiftUpdated'];

const EMPLOYEE_PROFILE_ONLY_AUDIT_LOG_TYPES = ['EmployeeWeeklyPreferencesUpdated'];

const useAuditLogs = ({ timesheetIds, jobIds, projectIds, employeeIds, isExternal = false, pageSize = 50 }) => {
  const { accountId } = useAccount();
  const [externalTimesheetId] = timesheetIds || [];
  const storeKey = ['audit-logs', accountId, isExternal, timesheetIds, jobIds, projectIds, employeeIds];

  const { isLoading, isIdle, data, isFetching, fetchNextPage, hasNextPage } = useInfiniteQuery(
    storeKey,
    ({ pageParam }) =>
      isExternal
        ? api.fetchOSTimesheetAuditLogs({
            accountId,
            timesheetId: externalTimesheetId,
            token: pageParam,
            pageSize
          })
        : api.fetchAuditLogs({ accountId, timesheetIds, jobIds, projectIds, employeeIds, token: pageParam, pageSize }),
    {
      enabled: !!accountId && (!isExternal || !!externalTimesheetId),
      staleTime: 0,
      cacheTime: Infinity,
      getNextPageParam: (lastPage) => (lastPage.data.length === 0 ? undefined : lastPage.token)
    }
  );

  const { mutateAsync: exportAuditLogs } = useMutation(
    ({ pageSize: exportPageSize }) =>
      api.exportAuditLogs({ accountId, timesheetIds, jobIds, projectIds, employeeIds, pageSize: exportPageSize }),
    {
      throwOnError: true
    }
  );

  const isEmployeeScoped = (employeeIds || []).length > 0;

  const auditLogs = useMemo(() => {
    const excludedTypes = isEmployeeScoped
      ? HIDDEN_AUDIT_LOG_TYPES
      : [...HIDDEN_AUDIT_LOG_TYPES, ...EMPLOYEE_PROFILE_ONLY_AUDIT_LOG_TYPES];

    return flatten((data?.pages || []).map(({ data: pageData }) => pageData)).filter(
      ({ type }) => !excludedTypes.includes(type)
    );
  }, [data?.pages, isEmployeeScoped]);

  return {
    isLoading,
    isIdle,
    isFetching,
    data: auditLogs,
    fetchMore: fetchNextPage,
    exportAuditLogs,
    canFetchMore: hasNextPage
  };
};

export default useAuditLogs;
